import { useMutation } from '@tanstack/react-query';
import { ErrorListDisplay } from 'components/ErrorDisplay';
import {
  addPatient,
  addPatientAPIOptionsSchema
} from 'lib/api/firebase/apis/apis';
import { useQueryDataErrors } from 'lib/hooks/use-query-data-errors';
import { useQueryErrorNotifier } from 'lib/hooks/use-query-error-notifier';
import { useErrorList } from 'lib/utils/useErrorList';
import { useCallback, useMemo } from 'react';
import { useGetPatientsList } from '../../../../../../lib/hooks/tanstack/use-getPatientsList';
import UserTable from '../user-table';
import { BaseTableUserRecord, UserListProps } from '../user-table-types';
import { listUserResponseToTableRow } from '../utils/listUserResponseToTableRow';
import { useResolvePatientTableActions } from './hooks/usePatientTableOptions';
import { useResolvePatientUserAddQuestions } from './hooks/useResolvePatientUserAddQuestions';
import { PatientRecord } from './types';

export function PatientTable(props: { clinicId: string }) {
  const confirmedUsers = useGetPatientsList(props.clinicId, false);
  const pendingUsers = useGetPatientsList(props.clinicId, true);

  const addUserMutation = useMutation({
    mutationFn: (args: { user: BaseTableUserRecord; clinicId: string }) => {
      const parsedData = addPatientAPIOptionsSchema.parse({
        ...args.user,
        clinicId: args.clinicId
      });
      return addPatient(parsedData);
    },
    onSuccess: () => {
      /**
       * Refetch all pending user data when a user is added
       * We do not need to refetch the confirmed users
       * WARN: This refetches the entire pending user table again when a single test-taker is added.
       */
      pendingUsers.refetch();
    }
  });

  const errorList = useErrorList();

  useQueryErrorNotifier(confirmedUsers, errorList, 'ConfirmedUsers: ');
  useQueryErrorNotifier(pendingUsers, errorList, 'PendingUsers: ');
  useQueryDataErrors({
    query: confirmedUsers,
    errorList,
    contextPrefix: 'ConfirmedUsers: '
  });
  useQueryDataErrors({
    query: pendingUsers,
    errorList,
    contextPrefix: 'PendingUsers: '
  });

  const patientUserAddQuestions = useResolvePatientUserAddQuestions(
    props.clinicId
  );

  /**
   * Actions that can appear for every row of the table
   */
  const actions = useResolvePatientTableActions(props.clinicId);

  /**
   * Extra columns for the table
   */
  const extraColumns: UserListProps<PatientRecord>['extraColumns'] = useMemo(
    () => [
      {
        title: 'Clinician',
        dataIndex: 'clinician',
        render: (_value, record) => {
          const rawRecord = record.raw;
          if (rawRecord.assignedClinicians === undefined) {
            console.warn(
              `[extraColumns - clinician] Missing assignedClinicians property`
            );
            return '';
          }
          return rawRecord.assignedClinicians.map(_ => _.name).join(', ');
        }
      }
    ],
    []
  );

  const data = useMemo(
    () => [
      ...(confirmedUsers.data?.pages ?? []).flatMap(_ =>
        listUserResponseToTableRow(_.data, { invitePending: false })
      ),
      ...(pendingUsers.data?.pages ?? []).flatMap(_ =>
        listUserResponseToTableRow(_.data, { invitePending: true })
      )
    ],
    [confirmedUsers.data, pendingUsers.data]
  );

  const onAddUser = useCallback(
    (userData, clinicId) =>
      addUserMutation.mutateAsync({ user: userData, clinicId }),
    // eslint-disable-next-line
    [addUserMutation.mutateAsync]
  );
  const refetchData = useCallback(() => {
    confirmedUsers.refetch();
    pendingUsers.refetch();
    // eslint-disable-next-line
  }, [confirmedUsers.refetch, pendingUsers.refetch]);

  return (
    <div>
      <ErrorListDisplay errorList={errorList} closeable />

      <UserTable<PatientRecord>
        typeUnit="Test-taker"
        onAddUser={onAddUser}
        clinicId={props.clinicId}
        extraColumns={extraColumns}
        extraStatus={[
          {
            name: p =>
              p.raw.canStartAssessments
                ? 'Assessments enabled'
                : 'Assessment disabled',
            shouldShow: p => !p.invitePending,

            toolTip:
              'Shows the current assessment status of the test-taker. Use the action options column to enable or disable this value'
          },
          {
            name: `Completing assessment`,
            shouldShow: p => p.raw.isInAssessment,
            toolTip: 'This test-taker is currently completing an assessmnet'
          },
          {
            name: p =>
              `${
                p.raw.isInAssessment ? 'Current' : 'Next'
              } assessment: ${p.raw.nextAssessmentType.toUpperCase()}`,
            shouldShow: p => !p.invitePending,
            toolTip:
              'Shows the next assessment that the test-taker will or is completing'
          }
        ]}
        actions={actions}
        userAddQuestions={patientUserAddQuestions}
        isLoading={confirmedUsers.isInitialLoading}
        data={data}
        refetchData={refetchData}
        onError={(error, metadata) => {
          errorList.addError(error, `${metadata.action} - ${metadata.id}`);
        }}
      />
    </div>
  );
}
